import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { withRouter } from 'react-router';

import isBoolean from 'common/util/isBoolean';

import { MEDIA_TYPE_3D_TOUR, MEDIA_TYPE_VIDEO } from 'common/mediaHelpers';

import {
  getLeaseLengthDescription,
  getListingAmenities,
  existsPetData,
  getListingPetWeightLimit,
  hasListingPetWeightLimit,
} from 'common/listingHelpers';

import { PROPERTY_TYPE_OPTION_VALUE_APARTMENT } from 'common/propertyTypes';

import { TOUR_SHOWING_APPLICATION_SOURCE_PAGE_APARTMENT_PAGE } from 'common/tourConstants';

import getApartmentListings, {
  makeListingStateManagerViewId,
} from 'client/actions/Listing/getApartmentListings';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFeatureCard from 'client/components/SrFeatureCard';
import SrPropertyTypeLabel from 'client/components/SrPropertyTypeLabel';
import SrFloorPlanCard from 'client/components/SrFloorPlanCard';
import SrListingImageCard from 'client/components/SrListingImageCard';
import SrInfiniteListingView from 'client/components/SrInfiniteListingView';
import SrDataTableMedia from 'client/components/SrDataTableMedia';
import SrMediaCarousel from 'client/components/SrMediaCarousel';
import SrGoogleViewMarket, {
  SR_GOOGLE_VIEW_MARKET_TYPE_ICON,
} from 'client/components/SrGoogleViewMarket';
import SrApartmentSpecialDealsList from 'client/components/SrApartmentSpecialDealsList';
import SrPropertyFeaturesBlock from 'client/components/SrPropertyFeaturesBlock';

@withRouter
class SrApartmentPage extends Component {
  static propTypes = {
    apartment: PropTypes.object.isRequired,
    floorPlans: PropTypes.array,
    propertyAttributes: PropTypes.array,
    apartmentSpecialDeals: PropTypes.array,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
  };

  _onClickListingCard = () => {
    const { history, apartment } = this.props;
    const apartmentPhotosUrl = `/apartment/${apartment.id}/photos`;
    history.push(apartmentPhotosUrl);
  };

  _onClickGoogleMapsView = () => {
    const { history, apartment } = this.props;
    history.push(`/apartment/${apartment.id}/map`);
  };

  _onClickGoogleMapsStreeView = () => {
    const { history, apartment } = this.props;
    history.push(`/apartment/${apartment.id}/map?streetView=true`);
  };

  _onLoadMoreApartmentListings = () => {
    const { apartment, dispatch } = this.props;

    dispatch(getApartmentListings, {
      apartmentBuildingId: apartment.fieldsForDisplay.apartmentBuildingIds,
    });
  };

  _onFloorPlansSectionRef = (floorPlansSectionEl) => {
    const { location } = this.props;
    this._floorPlansSectionEl = floorPlansSectionEl;
    if (location.search.includes('scrollTo=floorPlansSection')) {
      setTimeout(() => {
        this._goToFloorPlansSection();
      }, 10);
    }
  };

  _goToFloorPlansSection = () => {
    if (!this._floorPlansSectionEl) {
      return;
    }
    this._floorPlansSectionEl.scrollIntoView({ behavior: 'smooth' });
  };

  render() {
    const {
      apartment,
      floorPlans,
      history,
      dispatch,
      apartmentSpecialDeals,
      propertyAttributes,
    } = this.props;

    const {
      activeListings,
      locationLatitude,
      locationLongitude,
      section8,
      listingFields,
      propertyFeatures,
      apartmentBuildingIds,
      address,
      addressStreetNumber,
      addressStreetName,
      addressStreetSuffix,
      hasSelfShowingUnits,
      has3DTourUnits,
    } = apartment.fieldsForDisplay;

    const { communityListingFields, apartmentListingFields } = (propertyAttributes || []).reduce(
      (memo, propertyAttribute) => {
        if (propertyAttribute.floorPlanId) {
          memo.apartmentListingFields[propertyAttribute.name] = propertyAttribute.attrs.value;
        } else {
          memo.communityListingFields[propertyAttribute.name] = propertyAttribute.attrs.value;
        }
        return memo;
      },
      {
        communityListingFields: {},
        apartmentListingFields: {},
      },
    );

    const communityAmenities = getListingAmenities(communityListingFields).filter(
      ({ value, iconClassName, emoji }) => value && (iconClassName || emoji),
    );
    const apartmentAmenities = getListingAmenities(apartmentListingFields).filter(
      ({ value, iconClassName, emoji }) => value && (iconClassName || emoji),
    );

    let googleMapsDOM = null;
    if (locationLatitude && locationLongitude) {
      googleMapsDOM = (
        <SrBox position="relative" marginTop="41px">
          <div className="googleMapsRow">
            <SrText size="medium" weight="medium">
              {[addressStreetNumber, addressStreetName, addressStreetSuffix, address].join(' ')}
            </SrText>
          </div>
          <SrGoogleViewMarket
            streetViewEnabled
            type={SR_GOOGLE_VIEW_MARKET_TYPE_ICON}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
            onClickMap={this._onClickGoogleMapsView}
            onClickStreeView={this._onClickGoogleMapsStreeView}
          />
        </SrBox>
      );
    }

    let descriptionDOM = null;
    if (listingFields.description) {
      descriptionDOM = (
        <div style={{ marginTop: 35 }}>
          <SrText font="whitney-sc" weight="bold" display="block">
            description
          </SrText>
          <div style={{ height: 14 }} />
          <SrText className="pre-wrap" leading="normal">
            {listingFields.description}
          </SrText>
        </div>
      );
    }

    let specialDealsDOM = null;
    if (apartmentSpecialDeals && apartmentSpecialDeals.length) {
      specialDealsDOM = (
        <SrBox marginTop={35}>
          <SrApartmentSpecialDealsList
            apartmentSpecialDeals={apartmentSpecialDeals}
            showAllFloorPlanDeals
          />
        </SrBox>
      );
    }

    let petDescriptionDOM = null;
    if (existsPetData(listingFields)) {
      const petWeightLimit = getListingPetWeightLimit(listingFields);
      petDescriptionDOM = (
        <SrBox marginTop={35}>
          <SrText font="whitney-sc" weight="bold" display="block">
            pet policy
          </SrText>
          <SrBox className="SrFeatureCardGrid" marginTop={15}>
            {isBoolean(listingFields.catsAllowed) && (
              <SrFeatureCard
                emoji={listingFields.catsAllowed ? '😺' : '😺🚫'}
                subtitle={listingFields.catsAllowed ? 'cat friendly' : 'cats not allowed'}
              />
            )}
            {isBoolean(listingFields.dogsAllowed) && (
              <SrFeatureCard
                emoji={listingFields.dogsAllowed ? '🐶' : '🐶🚫'}
                subtitle={listingFields.dogsAllowed ? 'dog friendly' : 'dogs not allowed'}
              />
            )}
            {typeof listingFields.maximumPetsAllowed === 'number' && (
              <SrFeatureCard title={`${listingFields.maximumPetsAllowed}`} subtitle="max pets" />
            )}
            <SrFeatureCard
              title={
                petWeightLimit && hasListingPetWeightLimit(petWeightLimit) ? (
                  <SrText size="inherit" weight="inherit">
                    {petWeightLimit}
                    <SrText size="2xl" weight="bold">
                      lb
                    </SrText>
                  </SrText>
                ) : (
                  '🐾'
                )
              }
              subtitle={
                petWeightLimit && hasListingPetWeightLimit(petWeightLimit)
                  ? 'weight limit'
                  : 'no weight limit'
              }
            />
          </SrBox>
        </SrBox>
      );
    }

    let generalInfoDOM = null;
    const generalInfoFields = [];
    if (listingFields.yearRenovated) {
      generalInfoFields.push({
        subtitle: 'year renovated',
        title: listingFields.yearRenovated,
        emoji: '✨',
      });
    }
    if (listingFields.yearBuilt) {
      generalInfoFields.push({
        subtitle: 'year built',
        title: listingFields.yearBuilt,
        emoji: '🛠',
      });
    }
    if (listingFields.minLeaseMonths || listingFields.maxLeaseMonths) {
      const { leaseLengthLabel, leaseLengthValue } = getLeaseLengthDescription({
        minLeaseMonths: listingFields.minLeaseMonths,
        maxLeaseMonths: listingFields.maxLeaseMonths,
      });
      generalInfoFields.push({
        subtitle: leaseLengthLabel,
        title: leaseLengthValue,
        emoji: '📄',
      });
    }
    generalInfoFields.push({
      subtitle: 'section 8 / vouchers',
      title: section8 ? 'Enrolled in Section 8' : 'Not enrolled in Section 8',
      emoji: '🏠',
    });
    if (apartment.numberOfUnits) {
      generalInfoFields.push({
        subtitle: 'number of units',
        title: apartment.numberOfUnits,
        emoji: '🏢',
      });
    }
    if (generalInfoFields.length) {
      generalInfoDOM = (
        <SrBox marginTop={45}>
          <SrText font="whitney-sc" weight="bold" display="block">
            general info
          </SrText>
          <SrBox className="SrFeatureCardList" marginTop={15}>
            {generalInfoFields.map((field) => (
              <SrFeatureCard
                key={field.title}
                type="row"
                title={field.title}
                subtitle={field.subtitle}
                emoji={field.emoji}
              />
            ))}
          </SrBox>
        </SrBox>
      );
    }

    let floorPlansDOM = null;
    let availableUnitsDOM = null;
    let availableUnitsText = null;

    if (floorPlans && floorPlans.length) {
      availableUnitsText = `${floorPlans.length} ${pluralize(
        'floorplan',
        floorPlans.length,
      )} listed below`;

      floorPlansDOM = (
        <SrBox marginTop={45}>
          <div style={{ position: 'relative', top: '-110px' }} ref={this._onFloorPlansSectionRef} />
          <SrText font="whitney-sc" size="2xl" weight="bold">
            {`${floorPlans.length} ${pluralize('floorplan', floorPlans.length)}
            ${pluralize('option', floorPlans.length)}`}
          </SrText>

          <SrBox className="floorPlanList">
            {floorPlans.map((floorPlan) => (
              <SrFloorPlanCard
                key={floorPlan.id}
                apartment={apartment}
                floorPlan={floorPlan}
                apartmentSpecialDeals={apartmentSpecialDeals}
                history={history}
                dispatch={dispatch}
              />
            ))}
          </SrBox>
        </SrBox>
      );
    } else if (activeListings) {
      availableUnitsText = `${activeListings} available unit ${pluralize('type', activeListings)}`;

      const viewId = makeListingStateManagerViewId({
        apartmentBuildingId: apartmentBuildingIds,
      });

      availableUnitsDOM = (
        <div style={{ marginTop: 38 }}>
          <SrInfiniteListingView
            title={availableUnitsText}
            viewId={viewId}
            requestedFrom={TOUR_SHOWING_APPLICATION_SOURCE_PAGE_APARTMENT_PAGE}
            onLoadMore={this._onLoadMoreApartmentListings}
            showThumbnailApartmentUnit
          />
        </div>
      );
    }

    return (
      <div className="SrApartmentPage">
        <SrListingImageCard
          imageUrl={apartment.photoUrls[0]}
          onClick={this._onClickListingCard}
          photoCount={apartment.photoUrls.length + apartment.communityPhotoUrls.length}
          hasSelfTour={hasSelfShowingUnits}
          has3DTour={has3DTourUnits}
        />
        <div className="body">
          <SrBox>
            <SrText size="2xl" weight="bold" paddingRight={14}>
              {apartment.name}
            </SrText>
          </SrBox>
          <SrBox marginTop={8}>
            <SrText weight="medium">
              {addressStreetNumber +
                ' ' +
                addressStreetName +
                ' ' +
                (addressStreetSuffix || '') +
                ' '}
              <SrText color="grey">{address}</SrText>
            </SrText>
          </SrBox>
          <SrBox marginTop={5}>
            <SrText>{availableUnitsText}</SrText>
          </SrBox>
          <SrBox marginTop={8}>
            <SrPropertyTypeLabel propertyType={PROPERTY_TYPE_OPTION_VALUE_APARTMENT} isCircular />
          </SrBox>
          {descriptionDOM}
          <SrDataTableMedia apartmentId={apartment.id}>
            {({ mediaList }) => {
              const media3DTours = mediaList.filter(({ type }) => type === MEDIA_TYPE_3D_TOUR);
              const mediaVideos = mediaList.filter(({ type }) => type === MEDIA_TYPE_VIDEO);
              return (
                <div>
                  {mediaVideos.length > 0 && (
                    <div style={{ marginTop: 35 }}>
                      <SrText font="whitney-sc" weight="bold" display="block">
                        video
                      </SrText>
                      <SrBox marginTop={17}>
                        <SrMediaCarousel mediaList={mediaVideos} type={MEDIA_TYPE_VIDEO} />
                      </SrBox>
                    </div>
                  )}
                  {media3DTours.length > 0 && (
                    <div style={{ marginTop: 35 }}>
                      <SrText font="whitney-sc" weight="bold" display="block">
                        3d tour
                      </SrText>
                      <SrBox marginTop={17}>
                        <SrMediaCarousel mediaList={media3DTours} type={MEDIA_TYPE_3D_TOUR} />
                      </SrBox>
                    </div>
                  )}
                </div>
              );
            }}
          </SrDataTableMedia>
          {specialDealsDOM}
          {googleMapsDOM}
          {propertyFeatures.length > 0 && (
            <SrBox marginTop={45}>
              <SrText font="whitney-sc" weight="bold" display="block">
                property features
              </SrText>
              <SrBox marginTop={17}>
                <SrPropertyFeaturesBlock propertyFeatures={propertyFeatures} />
              </SrBox>
            </SrBox>
          )}
          {petDescriptionDOM}
          {generalInfoDOM}
          {communityAmenities.length > 0 && (
            <SrBox marginTop={45}>
              <SrText font="whitney-sc" weight="bold" display="block">
                community amenities
              </SrText>
              <SrBox className="SrFeatureCardGrid" marginTop={17}>
                {communityAmenities.map((amenity) => (
                  <SrFeatureCard
                    key={amenity.name}
                    emoji={amenity.emoji}
                    subtitle={amenity.title}
                    iconClassName={amenity.iconClassName}
                    className="isAmenity"
                  />
                ))}
              </SrBox>
            </SrBox>
          )}
          {apartmentAmenities.length > 0 && (
            <SrBox marginTop={45}>
              <SrText font="whitney-sc" weight="bold" display="block">
                apartment amenities
              </SrText>
              <SrBox className="SrFeatureCardGrid" marginTop={17}>
                {apartmentAmenities.map((amenity) => (
                  <SrFeatureCard
                    key={amenity.name}
                    emoji={amenity.emoji}
                    subtitle={amenity.title}
                    iconClassName={amenity.iconClassName}
                    className="isAmenity"
                  />
                ))}
              </SrBox>
            </SrBox>
          )}
          {floorPlansDOM}
          {availableUnitsDOM}
        </div>
      </div>
    );
  }
}

SrApartmentPage.displayName = 'SrApartmentPage';

export default SrApartmentPage;
